import type {
  TranscriptSegmentDisplay,
  TranscriptSegmentNote,
  StoredTranscriptSegment,
  TranscriptSpeakerType,
  SpeakerObject,
} from '@/types/call';

function normalizeSpeakerType(raw?: string): TranscriptSpeakerType | undefined {
  const s = (raw ?? '').toLowerCase();
  if (s === 'agent_2') return 'agent_2';
  if (s === 'agent') return 'agent';
  if (s === 'customer') return 'customer';
  if (s === 'bot') return 'bot';
  if (s === 'summary') return 'summary';
  return undefined;
}

/** Map live transcript chunks + segmentNotes to TranscriptSegmentDisplay[]. */
export function mapLiveChunksToSegments(
  transcriptChunks: Array<{
    transcript: string;
    isPartial: boolean;
    sentenceId?: string;
    resultId?: string;
    speaker?: string;
    speakerObject?: SpeakerObject;
    timestamp?: string;
  }>,
  segmentNotes: Record<string, TranscriptSegmentNote[]>
): TranscriptSegmentDisplay[] {
  const trimToUndefined = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  };

  return transcriptChunks.map((chunk, i) => {
    const speakerObject = chunk.speakerObject;
    const rawType = speakerObject?.type ?? chunk.speaker;
    const speakerType = normalizeSpeakerType(rawType);

    return {
      id: chunk.sentenceId ?? chunk.resultId ?? `chunk-${i}`,
      text: chunk.transcript || '',
      speaker: speakerType ?? chunk.speaker,
      speakerType,
      isPartial: chunk.isPartial,
      speakerName:
        speakerType === 'customer'
          ? (trimToUndefined(speakerObject?.userPhone) ??
            trimToUndefined(speakerObject?.userName))
          : trimToUndefined(speakerObject?.userName),
      timestamp: chunk.timestamp,
      notes:
        chunk.sentenceId && segmentNotes[chunk.sentenceId]
          ? segmentNotes[chunk.sentenceId]
          : [],
      notePayload:
        chunk.sentenceId && !chunk.isPartial
          ? { type: 'live' as const, sentenceId: chunk.sentenceId }
          : undefined,
    };
  });
}

type MapStoredToSegmentsOptions = {
  callType?: 'call' | 'chat';
};

/** Map stored transcriptions to TranscriptSegmentDisplay[]. */
export function mapStoredToSegments(
  transcriptions: StoredTranscriptSegment[],
  transcriptId: string,
  options: MapStoredToSegmentsOptions = {}
): TranscriptSegmentDisplay[] {
  const { callType = 'call' } = options;
  return transcriptions.map(seg => {
    const trimToUndefined = (value: unknown): string | undefined => {
      if (typeof value !== 'string') return undefined;
      const trimmed = value.trim();
      return trimmed.length ? trimmed : undefined;
    };

    const speakerObject = seg.speakerObject;
    const rawType = speakerObject?.type ?? seg.speaker;
    const baseSpeakerType = normalizeSpeakerType(rawType);

    // Backend bot identity heuristic:
    // - type === "agent"
    // - no real identity fields like userEmail / userId
    // - placeholder userName like "Agent"
    const userName = trimToUndefined(speakerObject?.userName);
    const userEmail = trimToUndefined(speakerObject?.userEmail);
    const userId = trimToUndefined(speakerObject?.userId);
    const isBoostAiBot =
      baseSpeakerType === 'agent' &&
      !userEmail &&
      !userId &&
      (userName?.toLowerCase() === 'agent' || !userName);

    const speakerType: TranscriptSpeakerType | undefined = isBoostAiBot
      ? 'bot'
      : baseSpeakerType;

    let speakerName: string | undefined;
    if (speakerType === 'customer') {
      speakerName =
        callType === 'chat'
          ? trimToUndefined(speakerObject?.userName)
          : (trimToUndefined(speakerObject?.userPhone) ??
            trimToUndefined(speakerObject?.userName));
    } else if (
      speakerType === 'agent' ||
      speakerType === 'agent_2' ||
      speakerType === 'bot'
    ) {
      speakerName = userName;
    } else {
      speakerName = undefined;
    }

    return {
      id: seg.id,
      text: seg.text,
      speaker: speakerType,
      speakerType,
      speakerName,
      isPartial: false,
      timestamp: seg.timestamp,
      notes: seg.notes ?? [],
      notePayload: {
        type: 'stored',
        transcriptId,
        segmentId: seg.id,
      },
    };
  });
}
